import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { StringHash } from '@app/shared/utils/utils-types';
import { ConfigServiceModel } from '@app/origami-api/services/config.service';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { getErrorDescription } from '@app/origami-api/utils/error-utils';

interface ErrorData {
  ErrorCode: number;
  ErrorMessage: string;
  ObjectName: string;
  ErrorGroup: string;
  ErrorSeverity: ErrorSeverity;
}

interface ErrorNode {
  id: string;
  text: string;
  quantity: number;
  items?: ErrorNode[];
}

export enum ErrorSeverity {
  None = 0,
  Minor = 5,
  Major = 10,
}

@Component({
  standalone: false,
  selector: 'ti-errors-information',
  templateUrl: './errors-information.component.html',
  styleUrls: ['./errors-information.component.sass'],
})
export class ErrorsInformationComponent implements OnInit {
  public readonly strings = tiStrings.services.dialogs.components.stringsErrorsInformations;
  readonly miscStrings = tiStrings.app.misc;
  readonly localStorageKeys = LocalStorageKeys;
  readonly ErrorSeverity = ErrorSeverity;

  @Input() data: StringHash<ConfigServiceModel.ErrorCategory[]>;
  @Input() treeDataText: string;
  @Input() roomId: number;
  @Output() close = new EventEmitter<any>();
  @Output() reset = new EventEmitter<any>();

  gridData: ErrorData[];
  treeData: ErrorNode[];
  expandedKeys: string[];
  selectedKeys: string[] = ['0_0'];
  isLoading = false;
  quantity: number;

  private errorsByCategories: StringHash<ErrorData[]>;
  private rootNodeId = 'root';

  constructor() {}

  ngOnInit() {
    this.expandedKeys = [this.rootNodeId];
    this.generateErrors(this.data);
  }

  onClose() {
    this.close.emit();
  }

  handleTreeSelection(event) {
    this.gridData = this.errorsByCategories[event.dataItem.text];
    this.quantity = event.dataItem.quantity;
  }

  private generateErrors(data: StringHash<ConfigServiceModel.ErrorCategory[]>) {
    this.errorsByCategories = {};
    let fullQuantity = 0;
    Object.keys(data).forEach((key) => {
      const errors = [];

      data[key].forEach((group) => {
        const errorsCategoryArray = group.Value.Errors.map((error) => {
          return Object.assign({}, error, {
            ErrorGroup: group.Key,
            ErrorGroupCode: group.Value.ErrorCode,
            ErrorMessage: getErrorDescription(error['ErrorCode']),
            ErrorSeverity: error.Severity,
          });
        });

        errors.push(...errorsCategoryArray);
      });

      if (errors.length > 0) {
        this.errorsByCategories[key] = errors;
        fullQuantity += errors.length;
      }
    });

    this.treeData = [
      {
        id: this.rootNodeId,
        text: this.treeDataText,
        quantity: fullQuantity,
        items: Object.keys(this.errorsByCategories).map((key) => ({
          id: key,
          text: key,
          quantity: this.errorsByCategories[key]?.length,
        })),
      },
    ];

    this.gridData = this.errorsByCategories[this.treeData[0]?.items[0]?.text];
    this.quantity = this.treeData[0]?.items[0]?.quantity;
  }

  public onReset(): void {
    this.reset.emit();
  }

  public getIconClass(node) {
    const isNodeExpanded = this.expandedKeys.indexOf(node.id) > -1;
    return {
      fa: true,
      'fa-folder': !isNodeExpanded,
      'fa-folder-open': isNodeExpanded,
    };
  }
}
